'use client';

import { FC, useMemo } from 'react';

import { ProductColor } from '@ravago/shared/page-data/models/elements';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { OTHER_COLOR_ID, OTHER_PRODUCER_COLOR_CODE, UNKNOWN_COLOR_ID, UNKNOWN_PRODUCER_COLOR_CODE } from '../../constants';

import { RfqColor } from './rfq-color';
import { RfqColorCode } from './rfq-color-code';

export interface RfqColorSelectorValue {
   colorId?: number;
   colorName?: string | undefined;
   producerColorCode?: string | undefined;
   desiredColor?: string | undefined;
   projectDescription?: string | undefined;
}

interface Props {
   id: string;
   colors: Array<ProductColor>;
   value: RfqColorSelectorValue;
   onChange: (value: RfqColorSelectorValue) => void;
   leadTimeIndicators?: LeadTimeIndicator[];
   leadTimeEnabled?: boolean;
   hideExtraColorOptions?: boolean;
}

export const RfqColorSelector: FC<Props> = ({ id, colors, value, onChange, leadTimeIndicators = [], leadTimeEnabled = false, hideExtraColorOptions = false }) => {
   const translate = useTranslations();

   const producerColorCodes = useMemo(() => {
      if (!value.colorId || value.colorId === OTHER_COLOR_ID || value.colorId === UNKNOWN_COLOR_ID) return undefined;
      return colors.find((col) => col.id === value.colorId)?.producerColorCodes;
   }, [value, colors]);

   const defaultColorName = useMemo(() => {
      if (!value.colorId || value.colorId === OTHER_COLOR_ID || value.colorId === UNKNOWN_COLOR_ID) return undefined;
      return colors.find((col) => col.id === value.colorId)?.name;
   }, [value, colors]);

   const desiredColor = value.desiredColor ?? '';
   const projectDescription = value.projectDescription ?? '';

   const handleColorChange = (newColorId: number) => {
      onChange({
         colorId: newColorId,
         producerColorCode: undefined,
         desiredColor: undefined,
         projectDescription: undefined
      });
   };

   const handleColorCodeChange = (newValue: string) => {
      onChange({ ...value, producerColorCode: newValue, desiredColor: undefined, projectDescription: undefined });
   };

   const handleDesiredColorChange = (newDesiredColor: string) => {
      onChange({ ...value, desiredColor: newDesiredColor });
   };

   const handleProjectDescriptionChange = (newProjectDescription: string) => {
      onChange({ ...value, projectDescription: newProjectDescription });
   };

   const showColorPicker = colors.length > 0;
   const showOtherField = value?.colorId === OTHER_COLOR_ID || value?.producerColorCode === OTHER_PRODUCER_COLOR_CODE;
   const showDescribeProjectField =
      !showColorPicker || value?.colorId === UNKNOWN_COLOR_ID || value?.producerColorCode === UNKNOWN_PRODUCER_COLOR_CODE;

   return (
      <Box gap="lg" direction="column">
         {showColorPicker && (
            <Box direction="column" gap="2xs">
               <Typography variant="body-highlight">{translate('extended-color-picker.label')}</Typography>
               <RfqColor
                  colors={colors}
                  leadTimeIndicators={leadTimeIndicators}
                  leadTimeEnabled={leadTimeEnabled}
                  value={value?.colorId}
                  onChange={handleColorChange}
                  hideExtraColorOptions={hideExtraColorOptions}
               />
            </Box>
         )}
         {producerColorCodes && value.colorId && defaultColorName && (
            <Box direction="column" gap="xs">
               <Typography variant="body-highlight">{translate('extended-color-picker.color-variant-label')}</Typography>
               <Box direction="column" gap="none">
                  <RfqColorCode
                     colorCodes={producerColorCodes}
                     hideExtraColorOptions={hideExtraColorOptions}
                     colorId={value.colorId}
                     defaultColorName={defaultColorName}
                     leadTimeIndicators={leadTimeIndicators}
                     leadTimeEnabled={leadTimeEnabled ?? false}
                     value={value?.producerColorCode ?? ''}
                     onChange={handleColorCodeChange}
                  />
               </Box>
            </Box>
         )}
         {showOtherField && (
            <Box direction="column" gap="sm">
               <Typography id="project-description-label" variant="body-highlight">
                  {`${translate('extended-color-picker.desired-color-label')} (${translate('request-for-quotation.optional')})`}
               </Typography>
               <InputField
                  id={`${id}-other-input`}
                  label={translate('extended-color-picker.desired-color-label')}
                  value={desiredColor}
                  maxLength={50}
                  onChange={handleDesiredColorChange}
                  trailingIcon="droplet"
               />
            </Box>
         )}
         {showDescribeProjectField && (
            <Box direction="column" gap="sm">
               <Typography id="project-description-label" variant="body-highlight">
                  {translate('extended-color-picker.project-description-label')} ({translate('request-for-quotation.optional')})
               </Typography>
               <InputField
                  id={`${id}-project-description-input`}
                  value={projectDescription}
                  maxLength={50}
                  label={translate('extended-color-picker.project-description-placeholder')}
                  onChange={handleProjectDescriptionChange}
               />
            </Box>
         )}
      </Box>
   );
};
